import { authApi, cacheShortTimer } from "@app/api";
import type { Next } from "@app/fx";
import { schema } from "@app/schema";
import type { AgreementStatus, AgreementSummary, AuthApiCtx } from "@app/types";

import type { BaaHalLinks } from "./hal";
export { messageFromAuthApiErrorBody } from "./hal";
export type { BaaHalLinks, BaaHalResource } from "./hal";

function linkHref(
  links: BaaHalLinks | undefined,
  rel: keyof BaaHalLinks,
): string | undefined {
  const href = links?.[rel]?.href;
  return typeof href === "string" && href ? href : undefined;
}

/** Normalizes auth-api HAL BAA payloads (snake_case, `_links`) into `AgreementSummary`. */
export function deserializeBaaSummary(raw: unknown): AgreementSummary {
  if (!raw || typeof raw !== "object") {
    return { status: "NONE" };
  }
  const o = raw as Record<string, unknown>;
  const links = o._links as BaaHalLinks | undefined;
  const status = (o.status as AgreementStatus) || "NONE";

  return {
    baaResourceType: typeof o._type === "string" ? o._type : undefined,
    agreementId:
      (typeof o.adobe_agreement_id === "string" && o.adobe_agreement_id) ||
      undefined,
    status,
    signingUrl: typeof o.signing_url === "string" ? o.signing_url : undefined,
    updatedAt: typeof o.updated_at === "string" ? o.updated_at : undefined,
    signedAt: typeof o.signed_at === "string" ? o.signed_at : undefined,
    downloadUrl: linkHref(links, "download"),
    selfUrl: linkHref(links, "self"),
    organizationUrl: linkHref(links, "organization"),
  };
}

function* deserializeBaaFromHal(ctx: AuthApiCtx, next: Next) {
  yield* next();
  if (!ctx.json?.ok || ctx.json.value == null) return;
  ctx.json.value = deserializeBaaSummary(ctx.json.value);
}

export function coerceBaaNotFoundToNone(
  ctx: Pick<AuthApiCtx, "json" | "response">,
): boolean {
  if (ctx.json.ok) return false;
  const isNotFound =
    ctx.response?.status === 404 || ctx.json.error?.error === "not_found";

  if (!isNotFound) return false;

  ctx.json = { ok: true, value: { status: "NONE" } } as AuthApiCtx["json"];
  return true;
}

function* mapBaaNotFoundToNone(ctx: AuthApiCtx, next: Next) {
  yield* next();
  coerceBaaNotFoundToNone(ctx);
}

export const fetchBaaStatus = authApi.get<{ orgId: string }, AgreementSummary>(
  "/organizations/:orgId/baa",
  { supervisor: cacheShortTimer() },
  [authApi.cache(), mapBaaNotFoundToNone, deserializeBaaFromHal],
);

export const createBaaAgreement = authApi.post<
  {
    orgId: string;
    email: string;
    firstName: string;
    lastName: string;
  },
  AgreementSummary
>("/organizations/:orgId/baa", function* (ctx, next) {
  const { email, firstName, lastName } = ctx.payload;
  ctx.request = ctx.req({
    body: JSON.stringify({
      email,
      signer_first_name: firstName,
      signer_last_name: lastName,
    }),
  });
  yield* next();
  if (ctx.json?.ok && ctx.json?.value) {
    const value = deserializeBaaSummary(ctx.json.value);
    ctx.json.value = value;
    yield* schema.update(schema.cache.add({ [ctx.key]: value }));
  }
});

export const refreshBaaStatus = authApi.post<
  { orgId: string },
  AgreementSummary
>("/organizations/:orgId/baa/refresh", [
  authApi.cache(),
  mapBaaNotFoundToNone,
  deserializeBaaFromHal,
]);
